import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Container,
  Dialog,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Slide,
  TextField,
  Toolbar,
  Typography,
  ThemeProvider,
  createTheme,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import LoadingButton from '@mui/lab/LoadingButton';
import HomeworkInfoForStudent from '../common/HomeworkInfoForStudent';
import HomeworkUploadsForStudent from '../common/HomeworkUploadsForStudent';
import { db } from '../service/Firebase-config';
import { addDoc, collection, onSnapshot, orderBy, query, updateDoc, doc } from 'firebase/firestore';
import { useAuth } from '../service/AuthContext';
import { useGlobalContext } from '../context/GlobalContext';
import { useHomeworkContext } from '../context/HomeworkContext';
import HomeworkInfo from '../common/HomeworkInfo';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const defaultTheme = createTheme();

export default function DialogEditHomeworkByStudent({
  open,
  closeEditHomework,
  fileStudent,
  setFileStudent,
  selectedTeacher,
}) {
  const { userInfo } = useAuth();
  const { selectedUser } = useGlobalContext();
  const { selectedHw } = useHomeworkContext();

  const [comments, setComments] = useState([]);
  const [commentMessage, setCommentMessage] = useState('');
  const [isNewCommentLoading, setIsNewCommentLoading] = useState(false);
  const [adminComment, setAdminComment] = useState(selectedHw?.adminComment || '');
  const [isAdminCommentLoading, setIsAdminCommentLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    if (selectedUser && selectedHw) {
      const collectionRef = collection(db, `students/${selectedUser.uid}/homeworks/${selectedHw.id}/comments`);
      const orderedQuery = query(collectionRef, orderBy('createdDate'));
      const unsubscribe = onSnapshot(orderedQuery, (snapshot) => {
        setComments(snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
      });
      return () => unsubscribe();
    }
  }, [selectedUser, selectedHw]);

  const sendCommentMessage = async () => {
    setIsNewCommentLoading(true);

    const commentData = {
      createdDate: new Date(),
      comment: commentMessage,
      fromWhom: 'student',
    }
    const dbCollection = collection(db, `students/${selectedUser.uid}/homeworks/${selectedHw.id}/comments`);
    await addDoc(dbCollection, commentData);
    setCommentMessage('');
    setIsNewCommentLoading(false);
  };

  const sendAdminComment = async () => {
    setIsAdminCommentLoading(true);
    const homeworkRef = doc(db, `students/${selectedUser.uid}/homeworks/`, selectedHw.id);
    await updateDoc(homeworkRef, { adminComment });
    setIsAdminCommentLoading(false);
    setSnackbarOpen(true); // Open the snackbar when the admin comment is saved
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const clear = () => {
    setCommentMessage('');
    closeEditHomework();
  };

  return (
    <Dialog fullScreen open={open} TransitionComponent={Transition}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={clear} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Close
          </Typography>
        </Toolbar>
      </AppBar>
      <ThemeProvider theme={defaultTheme}>
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
          }}
        >
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12} lg={9}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                  {userInfo?.role === 'admin'&& <HomeworkInfo selectedTeacher={selectedTeacher} />}
                  {userInfo?.role === 'student' && <HomeworkInfoForStudent student={selectedUser} homework={selectedHw} selectedTeacher={selectedTeacher} />}
                </Paper>

                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', mt: 3 }}>
                  <HomeworkUploadsForStudent
                    fileStudent={fileStudent}
                    setFileStudent={setFileStudent}
                  />
                </Paper>
                {userInfo?.role === 'admin' && (
                  <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', mt: 3 }}>
                    <TextField
                      margin="dense"
                      id="topic"
                      name="topic"
                      label="Topic (Teacher Only)"
                      multiline
                      rows={2}
                      fullWidth
                      variant="outlined"
                      disabled
                      value={selectedHw?.topic || ' '}
                    />
                    <TextField
                      margin="dense"
                      id="journal"
                      name="journal"
                      label="Journal (Teacher Only)"
                      multiline
                      rows={4}
                      fullWidth
                      variant="outlined"
                      disabled
                      value={selectedHw?.journal || ' '}
                    />
                    <TextField
                      margin="dense"
                      id="adminComment"
                      name="adminComment"
                      label="Comment (Admin Only)"
                      multiline
                      rows={4}
                      fullWidth
                      variant="outlined"
                      onChange={(e) => setAdminComment(e.target.value)}
                      value={adminComment}
                    />
                    <LoadingButton
                      loading={isAdminCommentLoading}
                      onClick={sendAdminComment}
                      variant="contained"
                      color="primary"
                      sx={{ mt: 2 }}
                    >
                      Save Admin Comment
                    </LoadingButton>
                  </Paper>
                )}
              </Grid>
              <Grid item xs={12} md={12} lg={3}>
                <Paper sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '500px', mb: 4 }}>
                  <Typography variant="h6" sx={{ p: 2, borderBottom: '1px solid lightgrey' }}>
                    Message
                  </Typography>
                  <Paper sx={{ overflow: 'auto', height: '100%', display: 'flex', flexDirection: 'column', p: 1 }}>
                    {comments &&
                      comments.map((data) => (
                        <Box
                          key={data.id}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: data.fromWhom === 'student' ? 'flex-end' : 'flex-start',
                          }}
                        >
                          <Typography
                            sx={{
                              backgroundColor: data.fromWhom === 'student' ? '#3f51b5' : '#e0e0e0',
                              color: data.fromWhom === 'student' ? '#fafafa' : '#000000',
                              display: 'inline-block',
                              p: '5px',
                              m: '5px',
                              maxWidth: '80%',
                              borderRadius: '10px',
                              wordBreak: 'break-word',
                            }}
                          >
                            {data.comment}
                          </Typography>
                                  <Typography
                            variant="caption"
                            sx={{ mx: 1, mt: 1, color: 'text.secondary' }}
                          >
                            {data.createdDate?.toDate().toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            }).replace('AM','am').replace('PM','pm')}
                          </Typography>
                        </Box>
                      ))}

                    {isNewCommentLoading && <Typography>Loading ...</Typography>}
                  </Paper>
                  <Box m={1}>
                    <TextField
                      disabled={userInfo?.role === 'admin'}
                      sx={{ width: '100%' }}
                      value={commentMessage}
                      placeholder="Send Message"
                      onChange={(e) => setCommentMessage(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton edge="end" color="primary" disabled={commentMessage.trim().length === 0} onClick={sendCommentMessage}>
                              <SendIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </ThemeProvider>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Admin Comment Saved!
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
